import { TintedBond } from '../comparison-bonds/BondCompare';
import { TintedPortfolio } from '../comparison-portfolios/PortfolioCompare';
import { TintedStock } from '../comparison-stocks/StockCompare';
import { FundCompareTopSection } from './FundCompareTopSection';
import { FundCompareChartSection } from './FundCompareChartSection';
import { EmptyColumn } from './EmptyColumn';
import { COLORS } from '../../../../config/constants';
import React, { HTMLProps } from 'react';
import { SwipableSection } from '../comparison-bar-swipable-section/SwipableSection';

export type SectionTypes = 'Sectors' | 'RegionDistribution' | 'CreditQuality' | 'Duration' | 'Companies';

export function mapTopText(
    data: TintedBond | TintedStock | TintedPortfolio,
    index: number,
    startDate: string,
    endDate: string,
): React.ReactNode {
    return (
        <FundCompareTopSection
            fund={data}
            tint={data.tint}
            startDate={startDate}
            endDate={endDate}
            className={`bg-white ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            key={index}
            index={index}
        />
    );
}

export function mapTopChart(data: TintedPortfolio, index: number): React.ReactNode {
    return (
        <FundCompareChartSection
            sections={[
                {
                    title: 'Aandelen',
                    items: data.assets.stocks.map((share, i) => {
                        return {
                            ...share,
                            weight: share.weight || 0,
                            tint: COLORS.shares[i],
                        };
                    }),
                },
                {
                    title: 'Obligaties',
                    items: data.assets.bonds.map((bond, i) => {
                        return {
                            ...bond,
                            weight: bond.weight || 0,
                            tint: COLORS.obligates[i],
                        };
                    }),
                },
            ]}
            title="Risicoverdeling"
            bottomBorder={true}
            className={`bg-white ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            keyString={index.toString() + ' Risicoverdeling'}
            key={index}
        />
    );
}

export function mapSectionPages(options: {
    index: number;
    sections: {
        title: string;
        items: {
            id: string;
            name: string;
            weight: number;
        }[];
        options?: {
            showColoredCircles?: boolean;
            showChart?: boolean;
        };
        referenceDate?: React.ComponentProps<typeof FundCompareChartSection>['referenceDate'];
    }[];
    appendix?: React.ComponentProps<typeof FundCompareChartSection>['appendix'];
}): React.ReactNode {
    const { index, sections, appendix } = options;

    if (sections.length === 0) return <EmptyColumn className="px-12 py-4" index={index} key={index} />;

    return (
        <div
            key={index}
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gridAutoRows: '1fr' }}
            className={`flex-1 grid gap-4  ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
        >
            {sections.map((section, dataIndex) => {
                if (section.items.length === 0) {
                    return (
                        <EmptyColumn className="px-12 py-4" index={dataIndex} key={dataIndex}>
                            {section.title} is niet van toepassing.
                        </EmptyColumn>
                    );
                }
                return (
                    <FundCompareChartSection
                        title={section.title}
                        showColoredCircles={
                            section.options?.showColoredCircles !== undefined
                                ? section.options?.showColoredCircles
                                : true
                        }
                        showChart={section.options?.showChart !== undefined ? section.options?.showChart : true}
                        key={index + ' ' + dataIndex + ' ' + section.title}
                        keyString={index + ' ' + dataIndex + ' ' + section.title}
                        sections={[
                            {
                                items:
                                    section.items.map((item, i) => {
                                        return {
                                            id: item.id,
                                            name: item.name,
                                            weight: item.weight,
                                            tint: COLORS.total[i],
                                        };
                                    }) || [],
                            },
                        ]}
                        appendix={appendix || 'remaining'}
                        referenceDate={section.referenceDate}
                    />
                );
            })}
        </div>
    );
}

export const Section: React.FC<
    {
        pages: React.ReactNode[];
    } & HTMLProps<HTMLDivElement>
> = ({ className, pages }) => {
    return (
        <>
            <div className={`hidden lg:flex flex-1 flex-row ${className}`}>{pages}</div>
            <div className="lg:hidden">
                <SwipableSection pages={pages} />
            </div>
        </>
    );
};

export const SectionsContainer: React.FC<HTMLProps<HTMLDivElement>> = ({ className, children }) => {
    return (
        <div className={`flex flex-1 flex-col shadow-lg-all-sides rounded-lg bg-white overflow-hidden ${className}`}>
            {children}
        </div>
    );
};
